/**
 * Page-context eligibility for the identity bootstrap (plan Flow A response
 * rules, D17 "which requests may mint", D19 iframe/crawler/prefetch/prerender
 * dispositions) and the frozen www article trigger
 * (REBELMOUSE_BOOTSTRAP_POST_ID_OR_CANONICAL_PATHNAME).
 * @module lib/identity/context
 */

function safeWindow() {
  return typeof window !== 'undefined' ? window : null;
}

/** True only in the top-level browsing context (never an iframe). */
function isTopLevelContext() {
  const win = safeWindow();
  if (!win) return false;
  try {
    return win.top === win.self;
  } catch (_) {
    return false;
  }
}

function isPrerendering() {
  return typeof document !== 'undefined' && document.prerendering === true;
}

/**
 * WebKit exposes no prerendering signal, but a page preloaded or prefetched in
 * the background (Chrome for iOS "preload pages", the Google app, the
 * Facebook in-app browser) loads with `visibilityState === 'hidden'` and is
 * torn down before any batch flushes. Production 2026-09-22: 92 of 95
 * review-queue conflicts came from such contexts — a fresh credential minted,
 * the persistent Insights id adopted, never a single event.
 */
function isDocumentHidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/** Resolves once the document is no longer hidden (immediately when it is not). */
function whenDocumentVisible() {
  return new Promise((resolve) => {
    if (!isDocumentHidden() || typeof document.addEventListener !== 'function') return resolve();
    const onChange = () => {
      if (isDocumentHidden()) return;
      if (typeof document.removeEventListener === 'function') document.removeEventListener('visibilitychange', onChange);
      resolve();
    };
    document.addEventListener('visibilitychange', onChange);
  });
}

/** Automation signals only; no fingerprinting. */
function isAutomatedClient() {
  try {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  } catch (_) {
    return false;
  }
}

/**
 * Registrable domain for the brand sites this SDK serves (two-label TLDs
 * only: narcity.com, mtlblog.com, getmilla.com, locals.tv). Good enough to
 * decide "same site as the issuer"; the server re-checks with its allowlist.
 */
function registrableDomain(hostname) {
  const host = String(hostname || '').toLowerCase().replace(/\.$/, '');
  if (!host) return '';
  if (host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return host;
  const labels = host.split('.');
  if (labels.length <= 2) return host;
  return labels.slice(-2).join('.');
}

function hostnameOf(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch (_) {
    return '';
  }
}

/**
 * The credential is first-party only when the page and the brand auth host
 * share a registrable domain (revised D2). A brand host loaded on another
 * site (locals.tv, previews) must never be bootstrapped — that is the ITP
 * classification risk the plan calls out.
 */
function isSameSiteWithIssuer(issuer) {
  const win = safeWindow();
  if (!win || !win.location) return false;
  const pageDomain = registrableDomain(win.location.hostname);
  const issuerDomain = registrableDomain(hostnameOf(issuer));
  if (!pageDomain || !issuerDomain) return false;
  if (pageDomain === 'localhost' || issuerDomain === 'localhost') return pageDomain === issuerDomain;
  return pageDomain === issuerDomain;
}

function metaContent(selector) {
  if (typeof document === 'undefined' || typeof document.querySelector !== 'function') return null;
  const el = document.querySelector(selector);
  return el && typeof el.getAttribute === 'function' ? el.getAttribute('content') : null;
}

function linkHref(selector) {
  if (typeof document === 'undefined' || typeof document.querySelector !== 'function') return null;
  const el = document.querySelector(selector);
  return el && typeof el.getAttribute === 'function' ? el.getAttribute('href') : null;
}

function stripQuery(url) {
  try {
    const parsed = new URL(url);
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch (_) {
    return null;
  }
}

/**
 * Frozen www trigger (dev source registry 2026-09-03.2): the RebelMouse post
 * id when the bootstrap data exposes it, else the canonical pathname. Returns
 * null on non-article pages.
 */
function resolveArticle() {
  const win = safeWindow();
  if (!win || typeof document === 'undefined') return null;
  const ogType = metaContent('meta[property="og:type"]');
  const bootstrapPost = win.REBELMOUSE_BOOTSTRAP_DATA && win.REBELMOUSE_BOOTSTRAP_DATA.post;
  const postId = bootstrapPost && (Number.isInteger(bootstrapPost.id) || /^\d{1,20}$/.test(String(bootstrapPost.id || ''))) ? String(bootstrapPost.id) : null;
  const isArticle = Boolean(postId)
    || (typeof ogType === 'string' && ogType.toLowerCase() === 'article')
    || Boolean(metaContent('meta[name="narcity:article_id"]'));
  if (!isArticle) return null;
  const pathname = win.location && typeof win.location.pathname === 'string' ? win.location.pathname : '';
  const id = postId || (pathname && pathname !== '/' ? pathname : null);
  if (!id) return null;
  const canonical = linkHref('link[rel="canonical"]') || (win.location ? win.location.href : null);
  const lang = typeof document.documentElement?.lang === 'string' ? document.documentElement.lang.trim().slice(0, 16) : '';
  return {
    type: 'ARTICLE',
    id,
    snapshot: {
      title: (metaContent('meta[property="og:title"]') || document.title || '').slice(0, 500) || null,
      canonicalUrl: canonical ? stripQuery(canonical) : null,
      imageUrl: metaContent('meta[property="og:image"]') || null,
      language: lang || null
    }
  };
}

module.exports = {
  hostnameOf,
  isAutomatedClient,
  isDocumentHidden,
  isPrerendering,
  isSameSiteWithIssuer,
  isTopLevelContext,
  registrableDomain,
  resolveArticle,
  stripQuery,
  whenDocumentVisible
};
